/**
 * Servicio para manejar operaciones relacionadas con consumo de combustible.
 * 
 * Funcionalidades:
 * - Obtener lista de consumos registrados (GET)
 * - Guardar/actualizar consumo (POST)
 * - Transformar datos para la estructura del dashboard
 * - Manejar endpoints y URLs específicas del servicio
 */

import { API_BASE_URL } from '../config/config';
import axios from './authService';


// 📍 ENDPOINTS ESPECÍFICOS DE COMBUSTIBLES
const COMBUSTIBLES_ENDPOINTS = {
  LIST: '/api/marcas/combustibles',
  CREATE: '/api/marcas/combustibles/',
};

// ⚙️ CONFIGURACIÓN LOCAL DEL SERVICIO
const SERVICE_CONFIG = {
  timeout: 10000, // 10 segundos
  headers: {
    'Content-Type': 'application/json',
    // Agregar headers específicos aquí si es necesario
    // 'X-API-Key': 'tu-api-key',
  },
  // Configuración de logging
  enableApiLogs: true,      // logs de peticiones API
  enableErrorLogs: true,    // logs de errores
  enableSuccessLogs: true,  // logs de éxito
};

/**
 * Construye URL completa para un endpoint específico
 * @param {string} endpoint - Endpoint relativo (ej: '/marcas/combustibles')
 * @returns {string} URL completa
 */
const buildUrl = (endpoint) => {
  const baseUrl = API_BASE_URL.endsWith('/')
    ? API_BASE_URL.slice(0, -1)
    : API_BASE_URL;

  const cleanEndpoint = endpoint.startsWith('/')
    ? endpoint
    : `/${endpoint}`;

  return `${baseUrl}${cleanEndpoint}`;
};

/**
 * Realiza petición GET para obtener todos los consumos registrados
 * 
 * @returns {Promise<Array>} Lista de consumos con estructura:
 * [
 *   {
 *     id: number,
 *     marca: string,
 *     familia: string,
 *     ano_modelo: string,
 *     modelo_label: string,
 *     catalogo_codigo: string,
 *     consumo_km_l: string,
 *     fecha_creacion: string,
 *     fecha_actualizacion: string
 *   }
 * ]
 */
export const obtenerConsumos = async () => {
  try {
    const url = buildUrl(COMBUSTIBLES_ENDPOINTS.LIST);

    if (SERVICE_CONFIG.enableApiLogs) {
      console.log(`📡 GET (axios):`, url);
    }

    const response = await axios.get(url, {
      headers: {
        ...SERVICE_CONFIG.headers,
      },
      timeout: SERVICE_CONFIG.timeout,
    });

    const data = response.data;

    if (SERVICE_CONFIG.enableSuccessLogs) {
      console.log('✅ Respuesta exitosa:', data);
    }

    // La API retorna { results: [...] }
    return data.results || [];
  } catch (error) {
    if (SERVICE_CONFIG.enableErrorLogs) {
      console.error('🔥 Error en petición (obtenerConsumos):', error);
      if (error.response) {
        console.error('   Status:', error.response.status);
        console.error('   Data:', error.response.data);
      }
    }

    // Normalizar mensaje
    const msg =
      error.response?.data?.detail ||
      error.message ||
      'Error desconocido al cargar consumos';

    throw new Error(`Error al cargar consumos: ${msg}`);
  }
};
/**
 * Envía un nuevo consumo o actualiza uno existente
 * 
 * @param {Object} consumoData - Datos del consumo
 * @param {string} consumoData.marca - Marca del vehículo
 * @param {string} consumoData.familia - Familia del modelo
 * @param {string} consumoData.ano_modelo - Año del modelo
 * @param {string} consumoData.modelo_label - Label del modelo (ej: "BRONCO 2025")
 * @param {string} consumoData.catalogo_codigo - Código del catálogo
 * @param {number|string} consumoData.consumo_km_l - Consumo en km/l
 * 
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const guardarConsumo = async (consumoData) => {
  try {
    const url = buildUrl(COMBUSTIBLES_ENDPOINTS.CREATE);

    // Preparar payload según el formato requerido por la API
    const payload = {
      marca: consumoData.marca,
      familia: consumoData.familia,
      ano_modelo: consumoData.ano_modelo,
      modelo_label: consumoData.modelo_label,
      catalogo_codigo: consumoData.catalogo_codigo,
      consumo_km_l: String(consumoData.consumo_km_l), // API espera string
    };

    if (SERVICE_CONFIG.enableApiLogs) {
      console.log(`📡 POST (axios):`, url);
      console.log('📦 Payload:', payload);
    }

    const response = await axios.post(url, payload, {
      headers: {
        ...SERVICE_CONFIG.headers,
      },
      timeout: SERVICE_CONFIG.timeout,
    });

    const data = response.data;

    if (SERVICE_CONFIG.enableSuccessLogs) {
      console.log('✅ Consumo guardado:', data);
    }

    return data;
  } catch (error) {
    if (SERVICE_CONFIG.enableErrorLogs) {
      console.error('🔥 Error en petición (guardarConsumo):', error);
      if (error.response) {
        console.error('   Status:', error.response.status);
        console.error('   Data:', error.response.data);
      }
    }

    const msg =
      error.response?.data?.detail ||
      error.message ||
      'Error desconocido al guardar consumo';

    throw new Error(`Error al guardar consumo: ${msg}`);
  }
};

/**
 * PUT: Actualizar un consumo existente por ID
 * 
 * @param {number|string} id - ID del registro de consumo
 * @param {Object} consumoData - Datos del consumo (igual que en guardarConsumo)
 */
// ya importas axios de './authService' y tienes COMBUSTIBLES_ENDPOINTS, SERVICE_CONFIG, buildUrl, etc.

export const actualizarConsumo = async (id, consumoData) => {
  try {
    const basePath = COMBUSTIBLES_ENDPOINTS.CREATE.endsWith('/')
      ? `${COMBUSTIBLES_ENDPOINTS.CREATE}${id}/`
      : `${COMBUSTIBLES_ENDPOINTS.CREATE}/${id}/`;

    const url = buildUrl(basePath);

    const payload = {
      marca: consumoData.marca,
      familia: consumoData.familia,
      ano_modelo: consumoData.ano_modelo,
      modelo_label: consumoData.modelo_label,
      catalogo_codigo: consumoData.catalogo_codigo,
      consumo_km_l: String(consumoData.consumo_km_l),
    };

    if (SERVICE_CONFIG.enableApiLogs) {
      console.log('📡 PUT (axios):', url);
      console.log('📦 Payload (update):', payload);
    }

    const response = await axios.put(url, payload, {
      headers: {
        ...SERVICE_CONFIG.headers,
      },
      timeout: SERVICE_CONFIG.timeout,
    });

    const data = response.data;

    if (SERVICE_CONFIG.enableSuccessLogs) {
      console.log('✅ Consumo actualizado:', data);
    }

    return data;
  } catch (error) {
    if (SERVICE_CONFIG.enableErrorLogs) {
      console.error('🔥 Error en petición (actualizarConsumo):', error);
      if (error.response) {
        console.error('   Status:', error.response.status);
        console.error('   Data:', error.response.data);
      }
    }

    const msg =
      error.response?.data?.detail ||
      error.message ||
      'Error desconocido al actualizar consumo';

    throw new Error(`Error al actualizar consumo: ${msg}`);
  }
};

/**
 * Transforma los datos de la API al formato usado en el dashboard
 * 
 * @param {Array} consumosAPI - Array de consumos de la API
 * @returns {Array} Array transformado para uso en el dashboard
 */
export const transformarConsumosParaDashboard = (consumosAPI = []) => {
  return consumosAPI.map(item => {
    // Crear key única para identificar el consumo
    const key = `${item.marca}|${item.familia}|${item.ano_modelo}|${item.catalogo_codigo}`;

    return {
      // Mantener estructura original de la API
      id: item.id,
      fecha_creacion: item.fecha_creacion,
      fecha_actualizacion: item.fecha_actualizacion,

      // Campos del dashboard (compatibles con el estado actual)
      key,
      marca: item.marca,
      familia: item.familia,
      ano_modelo: item.ano_modelo,
      modelo_label: item.modelo_label,
      catalogo_codigo: item.catalogo_codigo,
      consumo_km_l: parseFloat(item.consumo_km_l) || 0,

      // Alias para compatibilidad
      catalogo: item.catalogo_codigo,
    };
  });
};

/**
 * Verifica si existe un consumo para una combinación específica
 * 
 * @param {Array} consumos - Lista de consumos actuales
 * @param {Object} criterio - Criterio de búsqueda
 * @returns {Object|null} Consumo encontrado o null
 */
export const buscarConsumoExistente = (consumos, criterio) => {
  const key = `${criterio.marca}|${criterio.familia}|${criterio.ano_modelo}|${criterio.catalogo_codigo}`;
  return consumos.find(c => c.key === key) || null;
};

/**
 * 🧪 Función de prueba de conexión específica para combustibles
 */
export const testConnection = async () => {
  try {
    const url = buildUrl(COMBUSTIBLES_ENDPOINTS.LIST);
    console.log(`🧪 Probando conexión a combustibles: ${url}`);

    const response = await fetch(url, {
      method: 'GET',
      headers: SERVICE_CONFIG.headers,
      signal: AbortSignal.timeout(SERVICE_CONFIG.timeout)
    });

    if (response.ok) {
      console.log('✅ Conexión exitosa con API de combustibles');
      return { success: true, status: response.status };
    } else {
      console.log(`❌ Error de conexión: ${response.status}`);
      return { success: false, status: response.status, error: response.statusText };
    }

  } catch (error) {
    console.error('🔥 Error al probar conexión:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Configurar el servicio (para cambiar configuraciones dinámicamente)
 * @param {Object} config - Nueva configuración
 */
export const configurarServicio = (config) => {
  if (config.timeout) SERVICE_CONFIG.timeout = config.timeout;
  if (config.enableApiLogs !== undefined) SERVICE_CONFIG.enableApiLogs = config.enableApiLogs;
  if (config.enableErrorLogs !== undefined) SERVICE_CONFIG.enableErrorLogs = config.enableErrorLogs;
  if (config.enableSuccessLogs !== undefined) SERVICE_CONFIG.enableSuccessLogs = config.enableSuccessLogs;
  if (config.headers) SERVICE_CONFIG.headers = { ...SERVICE_CONFIG.headers, ...config.headers };

  console.log('🔧 Servicio de combustibles reconfigurado:', SERVICE_CONFIG);
};

// 📊 Exportar información del servicio para debugging
export const getServiceInfo = () => ({
  baseUrl: API_BASE_URL,
  endpoints: COMBUSTIBLES_ENDPOINTS,
  config: SERVICE_CONFIG,
  availableActions: ['obtenerConsumos', 'guardarConsumo', 'testConnection', 'configurarServicio']
});

// 📋 Exportar endpoints para uso externo si es necesario
export const ENDPOINTS = COMBUSTIBLES_ENDPOINTS;